import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    useTheme,
    useMediaQuery,
    CssBaseline,
    Tooltip
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Inbox as InboxIcon,
    Settings as SettingsIcon,
    ExitToApp as LogoutIcon,
    Notifications as NotificationsIcon,
    ChevronLeft as ChevronLeftIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    Security as SecurityIcon,
    Assignment as AssignmentIcon,
    Bolt as BoltIcon,
    Palette,
    DarkMode,
    LightMode,
    Chat as ChatIcon
} from '@mui/icons-material';
import { ThemeVariant, useThemeMode } from '../contexts/ThemeContext';
import LynaAvatar from './LynaAvatar';
import LynaChat from './LynaChat';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { addGlobalListener, removeGlobalListener, useSocket } from '../contexts/SocketContext';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import { useReservationReminders } from '../hooks/useReservationReminders';
import CatAssistant from './CatAssistant';
import MichiTour from './MichiTour/MichiTour';

const drawerWidth = 240;

// Inner component to use the hook
const AdminLayoutContent: React.FC = () => {
    const [open, setOpen] = useState(true);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [anchorElNotif, setAnchorElNotif] = useState<null | HTMLElement>(null);
    const [showChat, setShowChat] = useState(false);

    const { user, logout } = useAuth();
    const { say, reservationReceived } = useCatAssistant();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { mode, variant, toggleTheme, setVariant } = useThemeMode();
    const isDark = mode === 'dark';
    const [anchorElTheme, setAnchorElTheme] = useState<null | HTMLElement>(null);

    // Activate reservation reminders
    useReservationReminders();
    const socket = useSocket();

    // Auto-close drawer on mobile
    useEffect(() => {
        if (isMobile) {
            setOpen(false);
        } else {
            setOpen(true);
        }
    }, [isMobile]);

    // Fetch boards and join rooms for global notifications
    useEffect(() => {
        const joinBoardRooms = async () => {
            try {
                console.log('🐱 CatAssistant: Fetching boards to join rooms...');
                // We can use the inbox endpoint to get all boards quickly
                const response = await axios.get('/api/dashboard/inbox');
                const { boards } = response.data;

                console.log('🐱 CatAssistant: Boards fetched:', boards?.length);

                if (socket && boards && Array.isArray(boards)) {
                    boards.forEach((board: any) => {
                        console.log(`🐱 CatAssistant: Joining room for board ${board.id}`);
                        socket.emit('joinBoard', String(board.id));
                    });
                    console.log(`🐱 CatAssistant joined ${boards.length} board rooms`);
                } else {
                    console.warn('🐱 CatAssistant: No boards found or socket not ready');
                }
            } catch (error) {
                console.error('🐱 CatAssistant: Error joining board rooms:', error);
            }
        };

        if (user && socket) {
            joinBoardRooms();
        } else {
            console.log('🐱 CatAssistant: Waiting for user or socket...', { user: !!user, socket: !!socket });
        }
    }, [user, socket]);

    const isBlack = variant === 'pure-black' && isDark;
    const isTrippy = variant === 'trippy';

    // Get notification count from localStorage (synced with AdminInbox)
    const [notifCount, setNotifCount] = useState(0);

    const fetchInboxCount = useCallback(async () => {
        try {
            const response = await axios.get('/api/dashboard/inbox');
            const { reservations } = response.data;
            const pendingCount = (reservations || []).filter((r: any) => r.status === 'pending').length;
            setNotifCount(pendingCount);
            localStorage.setItem('notificationCount', pendingCount.toString());
        } catch (error) {
            console.error('🐱 Notif fetch error:', error);
            // Fallback to storage
            setNotifCount(parseInt(localStorage.getItem('notificationCount') || '0'));
        }
    }, []);

    useEffect(() => {
        fetchInboxCount();

        const handleStorageChange = () => {
            setNotifCount(parseInt(localStorage.getItem('notificationCount') || '0'));
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [fetchInboxCount]);

    useEffect(() => {
        const handleGlobalEvent = (event: string, data: any) => {
            console.log('🐱 AdminLayout Global Event:', event, data);

            if (event === 'reservationCreated') {
                reservationReceived(data.customerName);
                // Increment notif count
                setNotifCount(prev => {
                    const next = prev + 1;
                    localStorage.setItem('notificationCount', next.toString());
                    return next;
                });
            } else if (event === 'reservationUpdated') {
                // If status changed from/to pending, update count
                if (data.status === 'confirmed' || data.status === 'cancelled') {
                    setNotifCount(prev => {
                        const next = Math.max(0, prev - 1);
                        localStorage.setItem('notificationCount', next.toString());
                        return next;
                    });
                } else if (data.status === 'pending') {
                   setNotifCount(prev => {
                        const next = prev + 1;
                        localStorage.setItem('notificationCount', next.toString());
                        return next;
                   });
                }

                if (data.status === 'confirmed') {
                    say(`¡Yujuu! 😺 ¡Reserva confirmada para ${data.customerName}!`, 'success');
                } else if (data.status === 'cancelled') {
                    say(`Oh no... 😿 La reserva de ${data.customerName} ha sido cancelada.`, 'alert');
                } else {
                    say(`¡Atención! 📝 La reserva de ${data.customerName} ha sido actualizada.`, 'tip');
                }
            } else if (event === 'reservationDeleted') {
                say(`Oh no... 😿 Una reserva ha sido eliminada permanentemente.`, 'alert');
                fetchInboxCount();
            } else if (event === 'reservationRestored') {
                say(`¡Genial! 😺 La reserva de ${data.customerName} ha sido restaurada.`, 'success');
                fetchInboxCount();
            } else if (event === 'whatsappReminder') {
                const when = data.reminderType === '1day' ? 'mañana' : 'en 1 hora';
                say(`¡Miau! 🐾 Acabo de recordarle a ${data.customerName} su turno de ${when} por WhatsApp. ¡No se me escapa nada!`, 'tip');
            } else if (event === 'whatsappSent') {
                say(`¡WhatsApp enviado! 📱 Se notificó a ${data.customerPhone || 'el cliente'} sobre su reserva.`, 'success');
            }
        };

        addGlobalListener(handleGlobalEvent);

        return () => {
            removeGlobalListener(handleGlobalEvent);
        };
    }, [say, socket, reservationReceived, fetchInboxCount]);

    const handleOpenThemeMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElTheme(event.currentTarget);
    };

    const handleCloseThemeMenu = () => {
        setAnchorElTheme(null);
    };

    const handleSelectVariant = (v: ThemeVariant) => {
        setVariant(v);
        handleCloseThemeMenu();
        if (v === 'trippy') {
            say('¡Miauuuuu! 🌈✨ El catnip está pegando fuerte... ¡Mira todos estos colores!', 'celebration');
        } else if (v === 'pure-black') {
            say('Modo sigilo activado. 🐈‍⬛🖤 Súper elegante.', 'greeting');
        } else if (v === 'pure-white') {
            say('¡Wow! 🥛✨ Todo tan brillante. ¡Me encanta!', 'celebration');
        } else {
            say('Volvimos a lo clásico. 🐾', 'tip');
        }
    };

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleOpenNotifMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNotif(event.currentTarget);
    };

    const handleCloseNotifMenu = () => {
        setAnchorElNotif(null);
    };

    const handleLogout = () => {
        handleCloseUserMenu();
        say('¡Hasta pronto! Te estaré esperando. 😿', 'greeting');
        setTimeout(() => {
            logout();
            navigate('/login');
        }, 1000);
    };

    const menuItems = [
        ...(user?.role === 'super_admin' ? [{ text: 'Super Admin', icon: <SecurityIcon />, path: '/admin/superadmin', tourId: 'tour-superadmin' }] : []),
        { text: 'Notificaciones', icon: <InboxIcon />, path: '/admin/inbox', tourId: 'tour-inbox' },
        { text: 'Tus Negocios', icon: <DashboardIcon />, path: '/dashboard', tourId: 'tour-dashboard' },
        { text: 'Todos los turnos!', icon: <BoltIcon />, path: '/admin/live', tourId: 'tour-live' },
        { text: 'Clientes', icon: <PeopleIcon />, path: '/clients', tourId: 'tour-clients' },
        { text: 'Gestor de Tareas', icon: <AssignmentIcon />, path: '/tasks', tourId: 'tour-tasks' },
        { text: 'Configuración', icon: <SettingsIcon />, path: '/admin/settings', tourId: 'tour-settings' },
    ];

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="absolute"

                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ...(open && {
                        marginLeft: drawerWidth,
                        width: `calc(100% - ${drawerWidth}px)`,
                        transition: theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    }),
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1
                }}
            >
                <Toolbar sx={{ pr: '24px' }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            marginRight: '36px',
                            ...(open && { display: 'none' }),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        sx={{ flexGrow: 1, fontWeight: 'bold', background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                    >
                        Turn.ar Admin
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={mode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}>
                            <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
                                {mode === 'dark' ? <LightMode /> : <DarkMode />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Combinación de Colores">
                            <IconButton color="inherit" onClick={handleOpenThemeMenu}>
                                <Palette />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Notificaciones">
                            <IconButton color="inherit" onClick={handleOpenNotifMenu} data-tour="tour-notifications">
                                <Badge badgeContent={notifCount} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Configuración">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} data-tour="tour-profile">
                                <Avatar alt={user?.name} sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>
                                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Theme Menu */}
                    <Menu
                        anchorEl={anchorElTheme}
                        open={Boolean(anchorElTheme)}
                        onClose={handleCloseThemeMenu}
                        sx={{ mt: '45px' }}
                    >
                        <MenuItem onClick={() => handleSelectVariant('classic')} selected={variant === 'classic'}>
                            Clásico
                        </MenuItem>
                        <MenuItem onClick={() => handleSelectVariant('pure-black')} selected={variant === 'pure-black'}>
                            Negro Puro (OLED) 🐈‍⬛
                        </MenuItem>
                        <MenuItem onClick={() => handleSelectVariant('pure-white')} selected={variant === 'pure-white'}>
                            Blanco Puro (Snow) 🥛
                        </MenuItem>
                        <MenuItem onClick={() => handleSelectVariant('trippy')} selected={variant === 'trippy'}>
                            Modo Catnip (Trippy) 🐈‍⬛🌈
                        </MenuItem>
                    </Menu>

                    <Menu
                        sx={{ mt: '45px' }}
                        id="menu-appbar"
                        anchorEl={anchorElUser}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorElUser)}
                        onClose={handleCloseUserMenu}
                    >
                        <MenuItem onClick={() => { setShowChat(!showChat); handleCloseUserMenu(); }}>
                            <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>
                            <Typography textAlign="center">Chat con Lyna</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => { navigate('/admin/settings'); handleCloseUserMenu(); }}>
                            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                            <Typography textAlign="center">Configuración</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            <Typography textAlign="center">Cerrar Sesión</Typography>
                        </MenuItem>
                    </Menu>

                    <Menu
                        sx={{ mt: '45px' }}
                        id="menu-notif"
                        anchorEl={anchorElNotif}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorElNotif)}
                        onClose={handleCloseNotifMenu}
                    >
                        <MenuItem onClick={() => { navigate('/admin/inbox'); handleCloseNotifMenu(); }}>
                            <Typography textAlign="center">Ver todas las notificaciones</Typography>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    '& .MuiDrawer-paper': {
                        position: 'relative',
                        whiteSpace: 'nowrap',
                        width: drawerWidth,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        boxSizing: 'border-box',
                        ...(!open && {
                            overflowX: 'hidden',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.leavingScreen,
                            }),
                            width: theme.spacing(7),
                            [theme.breakpoints.up('sm')]: {
                                width: theme.spacing(9),
                            },
                        }),
                        background: isTrippy 
                            ? (isDark ? 'linear-gradient(180deg, #1A0836 0%, #0D0221 100%)' : 'linear-gradient(180deg, #00FFCC 0%, #FF00FF 100%)')
                            : (isBlack ? '#000000' : (isDark ? '#0A0A0A' : '#FFFFFF')),
                        color: (isDark || isBlack) ? 'white' : 'text.primary',
                        borderRight: (isDark || isBlack) ? '1px solid #222' : '1px solid #eee'
                    },
                }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: [1],
                    }}
                >
                    <IconButton onClick={toggleDrawer} sx={{ color: (isDark || isBlack) ? 'white' : 'text.primary' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Toolbar>
                <Divider sx={{ borderColor: (isDark || isBlack) ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
                <List component="nav">
                    {menuItems.map((item) => (
                        <ListItemButton
                            key={item.text}
                            onClick={() => navigate(item.path)}
                            selected={location.pathname === item.path}
                            data-tour={item.tourId}
                            sx={{
                                '&.Mui-selected': {
                                    bgcolor: (isDark || isBlack) ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                    color: (isDark || isBlack) ? 'primary.light' : 'primary.main',
                                    '& .MuiListItemIcon-root': {
                                        color: (isDark || isBlack) ? 'primary.light' : 'primary.main',
                                    },
                                    '&:hover': {
                                        bgcolor: (isDark || isBlack) ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                    }
                                },
                                '&:hover': {
                                    bgcolor: (isDark || isBlack) ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                },
                                my: 0.5,
                                mx: 1,
                                borderRadius: 2
                            }}
                        >
                            <ListItemIcon sx={{ color: (isDark || isBlack) ? 'white' : 'text.secondary', minWidth: 40 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                sx={{
                                    opacity: open ? 1 : 0,
                                    color: (isDark || isBlack) ? 'white' : 'text.primary',
                                    transition: 'opacity 0.2s ease-in-out',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    pt: 8
                }}
            >
                <Outlet />
            </Box>
            {showChat && <LynaChat />}
        </Box>
    );
};



const AdminLayout: React.FC = () => {
    return (
        <>
            <MichiTour />
            <AdminLayoutContent />
        </>
    );
};

export default AdminLayout;
